import { useEffect, useMemo, useState } from "react";
import { Tag, Wand2, Search, Save, RotateCcw, Loader2, Sparkles, Download } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const Cell = ({ value, onChange, suffix = "€", testId, step = 1 }) => (
  <div className="relative">
    <input
      type="number"
      step={step}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : +e.target.value)}
      data-testid={testId}
      className="w-full bg-white/[0.03] border border-white/10 rounded-md px-2 py-1.5 text-xs font-mono text-right focus:border-[#ff0033]/60 focus:outline-none"
    />
    {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-white/30 pointer-events-none">{suffix}</span>}
  </div>
);

export default function Tariffe() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dirty, setDirty] = useState({}); // {car_id: true}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/tariffe");
      setRows(r.data || []);
      setDirty({});
    } catch (e) {
      toast.error("Errore caricamento tariffe: " + e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = (search || "").toLowerCase().trim();
    if (!s) return rows;
    return rows.filter((r) =>
      `${r.marca} ${r.modello} ${r.targa} ${r.colore}`.toLowerCase().includes(s)
    );
  }, [rows, search]);

  const totals = useMemo(() => {
    if (rows.length === 0) return null;
    const g = rows.reduce((a, r) => a + (+r.prezzo_giornaliero || 0), 0);
    const s = rows.reduce((a, r) => a + (+r.prezzo_settimanale || 0), 0);
    const m = rows.reduce((a, r) => a + (+r.prezzo_mensile || 0), 0);
    return {
      cnt: rows.length,
      mediaG: (g / rows.length).toFixed(2),
      maxG: Math.max(...rows.map((r) => +r.prezzo_giornaliero || 0)),
      minG: Math.min(...rows.filter((r) => +r.prezzo_giornaliero > 0).map((r) => +r.prezzo_giornaliero || 999)),
      potenzialeMensile: m.toFixed(0),
    };
  }, [rows]);

  const updateRow = (car_id, field, value) => {
    setRows((prev) => prev.map((r) => r.car_id === car_id ? { ...r, [field]: value } : r));
    setDirty((d) => ({ ...d, [car_id]: true }));
  };

  const applySuggested = (car_id) => {
    const r = rows.find((x) => x.car_id === car_id);
    if (!r || !r.suggested) return;
    setRows((prev) => prev.map((row) => row.car_id === car_id ? {
      ...row,
      prezzo_giornaliero: r.suggested.prezzo_giornaliero,
      prezzo_settimanale: r.suggested.prezzo_settimanale,
      prezzo_mensile: r.suggested.prezzo_mensile,
      km_inclusi_giorno: r.suggested.km_inclusi_giorno,
      prezzo_km_extra: r.suggested.prezzo_km_extra,
    } : row));
    setDirty((d) => ({ ...d, [car_id]: true }));
  };

  const saveRow = async (car_id) => {
    const r = rows.find((x) => x.car_id === car_id);
    if (!r) return;
    try {
      await api.put(`/tariffe/${car_id}`, {
        car_id,
        prezzo_giornaliero: +r.prezzo_giornaliero || 0,
        prezzo_settimanale: +r.prezzo_settimanale || 0,
        prezzo_mensile: +r.prezzo_mensile || 0,
        km_inclusi_giorno: +r.km_inclusi_giorno || 100,
        prezzo_km_extra: +r.prezzo_km_extra || 0.25,
        prezzo_giornaliero_alta: r.prezzo_giornaliero_alta == null ? null : +r.prezzo_giornaliero_alta,
        prezzo_giornaliero_bassa: r.prezzo_giornaliero_bassa == null ? null : +r.prezzo_giornaliero_bassa,
        eta_minima_conducente: +r.eta_minima_conducente || 21,
        sovrapprezzo_giovane: +r.sovrapprezzo_giovane || 0,
        sovrapprezzo_extra_driver: +r.sovrapprezzo_extra_driver || 0,
        cauzione_consigliata: +r.cauzione_consigliata || 300,
      });
      setDirty((d) => { const c = { ...d }; delete c[car_id]; return c; });
      toast.success(`Tariffe ${r.targa} salvate`);
    } catch (e) { toast.error("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const saveAll = async () => {
    setSaving(true);
    const ids = Object.keys(dirty);
    for (const id of ids) await saveRow(id);
    setSaving(false);
    toast.success(`Salvate ${ids.length} tariffe`);
  };

  const autoFillAll = async (onlyMissing = true) => {
    try {
      const r = await api.post(`/tariffe/auto-fill?only_missing=${onlyMissing}`);
      toast.success(`AI ha generato ${r.data.updated} tariffe`);
      load();
    } catch (e) { toast.error("Errore: " + e.message); }
  };

  const exportCSV = () => {
    const head = "TARGA;MARCA;MODELLO;COLORE;GIORNO;SETTIMANA;MESE;KM/GIORNO;€/KM EXTRA\n";
    const body = rows.map((r) =>
      `${r.targa};${r.marca};${r.modello};${r.colore || ""};${r.prezzo_giornaliero};${r.prezzo_settimanale};${r.prezzo_mensile};${r.km_inclusi_giorno};${r.prezzo_km_extra}`
    ).join("\n");
    const blob = new Blob([head + body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `tariffe_fabrirent_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="tariffe-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            <Tag className="text-[#ff0033]" size={26} />
            Tariffe Veicoli
          </h1>
          <p className="text-sm text-white/45 mt-1">
            Listino centrale — prezzo giorno · settimana · mese · km extra · alta/bassa stagione · cauzione consigliata. Tutto modificabile inline.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => autoFillAll(true)} data-testid="autofill-missing-btn"
            className="px-4 py-2 rounded-full text-xs inline-flex items-center gap-2 bg-amber-500/15 border border-amber-500/40 text-amber-200 hover:bg-amber-500/25">
            <Sparkles size={14} /> Auto-fill mancanti
          </button>
          <button onClick={() => autoFillAll(false)} data-testid="autofill-all-btn"
            className="px-4 py-2 rounded-full text-xs inline-flex items-center gap-2 bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/25">
            <Wand2 size={14} /> Auto-fill tutti (sovrascrive)
          </button>
          <button onClick={exportCSV}
            className="px-4 py-2 rounded-full text-xs inline-flex items-center gap-2 bg-white/[0.04] border border-white/10 hover:bg-white/[0.08]">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={saveAll} disabled={Object.keys(dirty).length === 0 || saving} data-testid="save-all-btn"
            className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2 disabled:opacity-40">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Salva {Object.keys(dirty).length > 0 && `(${Object.keys(dirty).length})`}
          </button>
        </div>
      </div>

      {/* KPI */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Veicoli" value={totals.cnt} accent="white" />
          <KPI label="Media €/giorno" value={`€ ${totals.mediaG}`} accent="emerald" />
          <KPI label="Min €/giorno" value={`€ ${totals.minG}`} accent="sky" />
          <KPI label="Max €/giorno" value={`€ ${totals.maxG}`} accent="amber" />
          <KPI label="Potenziale mese" value={`€ ${(+totals.potenzialeMensile).toLocaleString("it-IT")}`} accent="fuchsia" />
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per targa, marca, modello, colore…"
          data-testid="tariffe-search"
          className="w-full bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none"
        />
      </div>

      {/* TABLE */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] text-xs" data-testid="tariffe-table">
            <thead className="bg-white/[0.04] text-white/55 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">Veicolo</th>
                <th className="px-2 py-2 text-left">Targa</th>
                <th className="px-2 py-2 text-right">€ Giorno</th>
                <th className="px-2 py-2 text-right">€ Settimana</th>
                <th className="px-2 py-2 text-right">€ Mese</th>
                <th className="px-2 py-2 text-right">Km/Giorno</th>
                <th className="px-2 py-2 text-right">€/Km Extra</th>
                <th className="px-2 py-2 text-right">€ Alta Stag.</th>
                <th className="px-2 py-2 text-right">€ Bassa Stag.</th>
                <th className="px-2 py-2 text-right">Cauzione</th>
                <th className="px-2 py-2 text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="text-center py-10 text-white/40">Caricamento…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={11} className="text-center py-10 text-white/40">Nessun veicolo</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.car_id} className={`border-t border-white/5 ${dirty[r.car_id] ? "bg-[#ff0033]/[0.04]" : ""}`}>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="font-semibold text-white">{r.marca} {r.modello}</div>
                    <div className="text-[10px] text-white/35">{r.colore} · {r.stato}</div>
                  </td>
                  <td className="px-2 py-2 font-mono text-white/80">{r.targa}</td>
                  <td className="px-2 py-2"><Cell value={r.prezzo_giornaliero} onChange={(v) => updateRow(r.car_id, "prezzo_giornaliero", v)} testId={`tar-day-${r.targa}`} /></td>
                  <td className="px-2 py-2"><Cell value={r.prezzo_settimanale} onChange={(v) => updateRow(r.car_id, "prezzo_settimanale", v)} /></td>
                  <td className="px-2 py-2"><Cell value={r.prezzo_mensile} onChange={(v) => updateRow(r.car_id, "prezzo_mensile", v)} /></td>
                  <td className="px-2 py-2"><Cell value={r.km_inclusi_giorno} onChange={(v) => updateRow(r.car_id, "km_inclusi_giorno", v)} suffix="km" /></td>
                  <td className="px-2 py-2"><Cell value={r.prezzo_km_extra} onChange={(v) => updateRow(r.car_id, "prezzo_km_extra", v)} step={0.01} /></td>
                  <td className="px-2 py-2"><Cell value={r.prezzo_giornaliero_alta} onChange={(v) => updateRow(r.car_id, "prezzo_giornaliero_alta", v)} /></td>
                  <td className="px-2 py-2"><Cell value={r.prezzo_giornaliero_bassa} onChange={(v) => updateRow(r.car_id, "prezzo_giornaliero_bassa", v)} /></td>
                  <td className="px-2 py-2"><Cell value={r.cauzione_consigliata} onChange={(v) => updateRow(r.car_id, "cauzione_consigliata", v)} /></td>
                  <td className="px-2 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => applySuggested(r.car_id)} title="Suggerito AI"
                        className="p-1.5 rounded hover:bg-amber-500/20 text-amber-300" data-testid={`tar-ai-${r.targa}`}>
                        <Sparkles size={13} />
                      </button>
                      <button onClick={() => saveRow(r.car_id)} disabled={!dirty[r.car_id]} title="Salva"
                        className="p-1.5 rounded hover:bg-emerald-500/20 text-emerald-300 disabled:opacity-30" data-testid={`tar-save-${r.targa}`}>
                        <Save size={13} />
                      </button>
                      <button onClick={load} title="Annulla"
                        className="p-1.5 rounded hover:bg-white/10 text-white/60">
                        <RotateCcw size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-white/35">
        💡 <b>Suggerimento AI</b>: clicca <Sparkles size={11} className="inline text-amber-300" /> su una riga per applicare il prezzo suggerito dall'algoritmo (segmento veicolo + tipo carburante). I prezzi sono modificabili in qualsiasi momento.
      </p>
    </div>
  );
}

const KPI = ({ label, value, accent = "white" }) => {
  const tones = {
    white: "text-white",
    emerald: "text-emerald-300",
    sky: "text-sky-300",
    amber: "text-amber-300",
    fuchsia: "text-fuchsia-300",
  };
  return (
    <div className="glass rounded-xl p-3">
      <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`font-display font-black text-xl ${tones[accent] || "text-white"} mt-1`}>{value}</div>
    </div>
  );
};
